/**
 * Mastery.
 *
 * The design document is emphatic (§1, §10): the platform must never say "you
 * completed Grade 6". It says "you have mastered 87% of this". So mastery — not
 * lessons finished, not XP — is the number every screen is built around.
 *
 * Mastery is four separate things, because a student can be strong in one and
 * weak in another, and the difference is exactly what a teacher would notice:
 *
 *   understanding — met the idea, and can do it when it is signposted
 *   accuracy      — gets it right, weighted towards recent work
 *   retention     — still gets it right after time has passed
 *   application   — can use it on harder, less signposted problems
 *
 * Every function here is pure and takes `now` as an argument. Nothing calls
 * `Date.now()`, so retention decay is testable by passing a different clock.
 */

export const MASTERY_WEIGHTS = {
  understanding: 0.3,
  accuracy: 0.3,
  retention: 0.2,
  application: 0.2,
} as const;

/** At or above this, a concept counts as mastered and unlocks what follows. */
export const MASTERY_THRESHOLD = 0.85;

/** Attempts below this many, mastery is reported as provisional. */
export const MIN_ATTEMPTS_FOR_MASTERY = 6;

export const DAY_MS = 86_400_000;

export type MasteryLevel = 'not-started' | 'attempted' | 'familiar' | 'proficient' | 'mastered';

/** The stored, replayable state for one concept. */
export type ConceptState = {
  conceptId: string;
  /** Every graded attempt, correct or not. Malformed input is not an attempt. */
  attempts: number;
  correct: number;
  /** Most recent results, newest last. Bounded — see `RECENT_WINDOW`. */
  recent: boolean[];
  /** Attempts at difficulty 4–5, which is where application shows up. */
  hardAttempts: number;
  hardCorrect: number;
  /** Highest difficulty answered correctly on the first try. */
  ceiling: number;
  lessonCompleted: boolean;
  firstStudiedAt?: number;
  lastPracticedAt?: number;
  /** Spaced-repetition state, see `review.ts`. */
  intervalDays: number;
  ease: number;
  dueAt?: number;
  reviews: number;
  /** misconception id → how many times it has been seen. */
  misconceptions: Record<string, number>;
  /**
   * Written by the placement test rather than by practice.
   *
   * A placement test produces an estimate, and an estimate has to be labelled
   * as one or the rest of the system mistakes it for work the student did. A
   * correct answer high in the graph implies its whole foundation — that
   * inference is why a student placed at Grade 12 is not sent back to counting
   * — but it is still an inference, and the single answer that produced it is
   * still a single answer.
   *
   * So this flag means "do not read this state as a student's history". The
   * first real attempt clears it, because practice supersedes an estimate.
   */
  placed?: boolean;
};

export const RECENT_WINDOW = 12;

export function createConceptState(conceptId: string): ConceptState {
  return {
    conceptId,
    attempts: 0,
    correct: 0,
    recent: [],
    hardAttempts: 0,
    hardCorrect: 0,
    ceiling: 0,
    lessonCompleted: false,
    intervalDays: 0,
    ease: 2.5,
    reviews: 0,
    misconceptions: {},
  };
}

export type MasteryBreakdown = {
  understanding: number;
  accuracy: number;
  retention: number;
  application: number;
  overall: number;
  level: MasteryLevel;
  /** True while there is too little evidence to trust the number. */
  provisional: boolean;
};

export const EMPTY_MASTERY: MasteryBreakdown = {
  understanding: 0,
  accuracy: 0,
  retention: 0,
  application: 0,
  overall: 0,
  level: 'not-started',
  provisional: true,
};

/**
 * Recency-weighted accuracy over the recent window.
 *
 * A student who has just fixed a misunderstanding should see their number move
 * today, not in three weeks — so the newest attempt carries roughly three times
 * the weight of the oldest one still in the window.
 */
export function weightedAccuracy(recent: readonly boolean[]): number {
  if (recent.length === 0) return 0;
  let weighted = 0;
  let total = 0;
  recent.forEach((wasCorrect, index) => {
    const weight = 1 + (2 * index) / Math.max(1, recent.length - 1);
    total += weight;
    if (wasCorrect) weighted += weight;
  });
  return weighted / total;
}

/**
 * Retention decays with time since the last successful practice, and decays
 * more slowly the longer the current review interval is — which is the whole
 * point of spaced repetition: each successful review buys more time.
 */
export function retentionAt(state: ConceptState, now: number): number {
  if (!state.lastPracticedAt || state.attempts === 0) return 0;
  const elapsedDays = Math.max(0, (now - state.lastPracticedAt) / DAY_MS);
  const halfLife = Math.max(1, state.intervalDays || 1) * 1.5;
  return Math.exp((-Math.LN2 * elapsedDays) / halfLife);
}

export function computeMastery(state: ConceptState | undefined, now: number): MasteryBreakdown {
  if (!state || (state.attempts === 0 && !state.lessonCompleted)) return EMPTY_MASTERY;

  const accuracy = weightedAccuracy(state.recent);

  // Understanding: half for having worked through the lesson, half for being
  // able to do the routine problems that follow it.
  const lessonPart = state.lessonCompleted ? 0.5 : 0;
  const routinePart = 0.5 * Math.min(1, accuracy * evidenceFactor(state.attempts, 4));
  const understanding = clamp01(lessonPart + routinePart);

  const retention = retentionAt(state, now);

  // Application: only hard problems count. With none attempted, the ceiling
  // reached gives partial credit rather than a zero that never moves.
  const application =
    state.hardAttempts > 0
      ? clamp01((state.hardCorrect / state.hardAttempts) * evidenceFactor(state.hardAttempts, 3))
      : clamp01(state.ceiling / 5) * 0.6;

  const overall = clamp01(
    MASTERY_WEIGHTS.understanding * understanding +
      MASTERY_WEIGHTS.accuracy * accuracy +
      MASTERY_WEIGHTS.retention * retention +
      MASTERY_WEIGHTS.application * application,
  );

  const provisional = state.attempts < MIN_ATTEMPTS_FOR_MASTERY;

  return {
    understanding,
    accuracy,
    retention,
    application,
    overall,
    level: masteryLevel(overall, provisional),
    provisional,
  };
}

/**
 * Mastery cannot be claimed on thin evidence: three lucky answers are not
 * mastery, so a provisional concept tops out at "proficient".
 */
export function masteryLevel(overall: number, provisional = false): MasteryLevel {
  if (overall <= 0) return 'not-started';
  if (overall < 0.4) return 'attempted';
  if (overall < 0.7) return 'familiar';
  if (overall < MASTERY_THRESHOLD || provisional) return 'proficient';
  return 'mastered';
}

export function isMastered(state: ConceptState | undefined, now: number): boolean {
  return computeMastery(state, now).level === 'mastered';
}

/** Ramps from 0 to 1 as evidence accumulates, so one attempt cannot mean 100%. */
function evidenceFactor(count: number, needed: number): number {
  return Math.min(1, count / needed);
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** Aggregate mastery over a set of concepts — a course bar, or a strand bar. */
export function aggregateMastery(
  conceptIds: readonly string[],
  states: Readonly<Record<string, ConceptState>>,
  now: number,
): number {
  if (conceptIds.length === 0) return 0;
  const total = conceptIds.reduce((sum, id) => sum + computeMastery(states[id], now).overall, 0);
  return total / conceptIds.length;
}

export function masteredSet(states: Readonly<Record<string, ConceptState>>, now: number): Set<string> {
  const out = new Set<string>();
  for (const [id, state] of Object.entries(states)) {
    if (isMastered(state, now)) out.add(id);
  }
  return out;
}

/**
 * A concept the student has demonstrated *or* the placement test implied — the
 * set to reason with when the question is "what is worth doing next".
 *
 * Deliberately not the same set as `masteredSet`, and the difference is the
 * point. Mastery is a claim the platform makes about a student and it is only
 * ever earned by practice (§1, §10) — counting a placement inference as mastery
 * would put "mastered" badges on a map the student never touched. But the
 * recommender is asking a different question, and answering *that* one with
 * mastery alone is what sent a Grade 12 placement back to kindergarten: none of
 * the 53 concepts implied by their answers counted, so the frontier collapsed
 * to the roots of the graph.
 *
 * An assumption is dropped the moment practice contradicts it — a concept the
 * student has actually got wrong is not something we are still assuming.
 */
export function knownSet(states: Readonly<Record<string, ConceptState>>, now: number): Set<string> {
  const out = masteredSet(states, now);
  for (const [id, state] of Object.entries(states)) {
    if (isPlacedAndUncontradicted(state)) out.add(id);
  }
  return out;
}

/**
 * A placement estimate that practice has not since contradicted.
 *
 * The estimate stands until the student gets the concept wrong — at which point
 * it was simply mistaken, and the ordinary machinery of repair and review takes
 * over from it.
 */
export function isPlacedAndUncontradicted(state: ConceptState | undefined): boolean {
  return Boolean(state?.placed) && (state as ConceptState).attempts === (state as ConceptState).correct;
}

/**
 * A placement estimate the student was actually asked about, and got right.
 *
 * The stronger of the two marks the placement test leaves, and worth telling
 * apart from the weaker one. Acing a Grade 11 question implies the whole
 * foundation underneath it, but "we asked and you answered" and "something else
 * you answered implies this" are not the same claim, and they should not
 * survive the same amount of contrary evidence. When work built on an inference
 * starts failing, the inference is a suspect; a direct answer is not.
 */
export function isVerifiedByPlacement(state: ConceptState | undefined): boolean {
  return isPlacedAndUncontradicted(state) && (state as ConceptState).attempts > 0;
}
